import { useState } from 'react'
import { X } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { BUCKET_ICONS, getBucketIcon } from '@/lib/bucketIcons'
import { cn } from '@/lib/utils'

interface BucketIconPickerProps {
  icon: string | undefined
  color: string
  onChange: (icon: string | undefined) => void
}

export function BucketIconPicker({ icon, color, onChange }: BucketIconPickerProps) {
  const [open, setOpen] = useState(false)
  const ActiveIcon = getBucketIcon(icon)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-center w-8 h-8 rounded-md border border-border hover:border-ring transition-colors flex-shrink-0"
          aria-label={icon ? `Icon: ${icon}. Click to change.` : 'Add icon'}
          title={icon ? `Icon: ${icon}` : 'Add icon (optional)'}
        >
          {ActiveIcon
            ? <ActiveIcon size={15} style={{ color }} aria-hidden />
            : <span className="text-muted-foreground/40 text-xs font-mono">+</span>}
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-64 p-3" align="start">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2.5">
          Icon <span className="font-normal normal-case">(optional — non-color accessibility cue)</span>
        </p>

        <div className="grid grid-cols-5 gap-1.5 mb-3">
          {BUCKET_ICONS.map(({ name, label, Icon }) => (
            <button
              key={name}
              type="button"
              onClick={() => { onChange(name); setOpen(false) }}
              title={label}
              aria-label={label}
              aria-pressed={icon === name}
              className={cn(
                'flex items-center justify-center w-9 h-9 rounded-md border transition-colors',
                icon === name
                  ? 'border-ring bg-secondary'
                  : 'border-transparent hover:border-border hover:bg-muted',
              )}
            >
              <Icon size={16} style={{ color }} aria-hidden />
            </button>
          ))}
        </div>

        {icon && (
          <button
            type="button"
            onClick={() => { onChange(undefined); setOpen(false) }}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={11} />
            Remove icon
          </button>
        )}
      </PopoverContent>
    </Popover>
  )
}
